import type { ICookies } from './boot.client';

export interface IRequest {
  endpoint: String | string;
  public_key?: String | string | null | undefined;
  auth_token?: String | string | null | undefined;
  content_encoding?: String | string | null | undefined;
};

export interface INotification {
  status: ComputedRef<boolean>,
  subscribe: () => Promise<any>,
  unsubscribe: () => Promise<any>,
  // request: (method: 'DELETE' | 'POST', data: IRequest) => Promise<any>,
  // requestNotification: (sub: PushSubscription, isDelete: boolean) => Promise<any>,
  ask: (e?: Event | undefined) => Promise<void>
};

export default defineNuxtPlugin({
  name: 'notification',
  dependsOn: [
    'nuxt:global-components',
    'nuxt:router',
    'nuxt-site-config:init',
    'boot',
    'auth'
  ],
  parallel: true,
  enforce: 'post',
  async setup(nuxtApp: any) {
    if (import.meta.client) {
      if (! (('Notification' in window) && ('serviceWorker' in navigator))) {
        console.warn('Browser does not support Notifications or Service Workers!');
        return;
      }
    } else
      return;
    // Init for cookies data - notifications
    const initDataCookie = (): void => {
      const auth = nuxtApp.$auth;
      if (auth ? auth.value?.logged() : false) {
        const cookieData = (nuxtApp.$cookies as ICookies)?.data;
        if (cookieData?.value?.userData) {
          if (! cookieData.value.userData[auth.value?.id])
            cookieData.value.userData[auth.value?.id] = {};
          if (! cookieData.value.userData[auth.value?.id].notification)
            cookieData.value.userData[auth.value?.id]['notification'] = {};
        }
      }
    };
    // Async subscribe for notifications
    const subscribe = async (): Promise<any> => {
      initDataCookie();
      const auth = nuxtApp.$auth;
      if (auth ? auth.value?.logged() : false) {
        const cookieData = (nuxtApp.$cookies as ICookies)?.data;
        const reg = await navigator?.serviceWorker?.ready;
        const existingSubscription = await reg?.pushManager?.getSubscription();
        if (cookieData.value && existingSubscription)
          cookieData.value.userData[auth.value?.id]['notification']['endpoint'] = existingSubscription?.endpoint;
        // Check with BE
        const pushNotifications = await requestBE('v1/notification/push', 'GET');
        const result = pushNotifications?.find((notification: any) => (notification.endpoint === existingSubscription?.endpoint));
        if (! result) {
          const newSubscription = await reg?.pushManager?.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(useRuntimeConfig()?.public?.contact?.vapidKey)
          });
          return await requestNotification(newSubscription, false);
        } else if (existingSubscription && ((Date.parseData(result?.updated_at)?.getUTCMinutes()) < ((new Date).getUTCMinutes() - 1440)))
          return await requestNotification(existingSubscription, false);
      }
    };
    // Async unsubscribe for notifications
    const unsubscribe = async (): Promise<any> => {
      initDataCookie();
      const auth = nuxtApp.$auth;
      if (auth ? auth.value?.logged() : false) {
        const cookieData = (nuxtApp.$cookies as ICookies)?.data;
        const reg = await navigator?.serviceWorker?.ready;
        const existingSubscription = await reg?.pushManager?.getSubscription();
        let subscription = existingSubscription;
        if (((! subscription) && cookieData?.value?.userData) ? cookieData.value.userData[auth.value?.id]?.notification?.endpoint : false)
          subscription = ({ endpoint: cookieData.value.userData[auth.value?.id]?.notification?.endpoint } as PushSubscription);
        // Check with BE
        const pushNotifications = await requestBE('v1/notification/push', 'GET');
        const result = pushNotifications?.find((notification: any) => (notification.endpoint === subscription?.endpoint));
        if (result && subscription)
          return await requestNotification(subscription, true).then(async (response: any) => {
            if (existingSubscription)
              await existingSubscription.unsubscribe();
            return response;
          });
      }
    };
    // Baseline request
    const request = async (method: 'DELETE' | 'POST', data: IRequest): Promise<any> => {
      initDataCookie();
      return await requestBE('v1/notification/push', method, data, { 'Content-Type': 'application/json' }).then((response: any) => {
        const cookieData = (nuxtApp.$cookies as ICookies)?.data;
        const auth = nuxtApp.$auth;
        if (cookieData?.value?.userData) {
          if (method !== 'DELETE')
            cookieData.value.userData[auth.value?.id]['notification']['endpoint'] = data.endpoint;
          else
            cookieData.value.userData[auth.value?.id]['notification']['endpoint'] = null;
        }
        return response;
      });
    };
    // Request notification where is request translated.
    const requestNotification = async (sub: PushSubscription, isDelete: boolean): Promise<any> => {
      let method = (isDelete ? 'DELETE' : 'POST') as 'DELETE' | 'POST';
      let data = {
        endpoint: sub.endpoint,
        public_key: null,
        auth_token: null,
        content_encoding: null
      } as IRequest;
      if (! isDelete) {
        // Get the P-256 elliptic curve public key (for 'public_key' validation)
        const p256dhKey = sub.getKey('p256dh');
        // Get the authentication secret key (for 'auth_token' validation)
        const authKey = sub.getKey('auth');
        if (p256dhKey && authKey) {
          data.public_key = urlBase64Encode(p256dhKey as ArrayBuffer);
          data.auth_token = urlBase64Encode(authKey as ArrayBuffer);
        }
        data.content_encoding = ((sub.contentEncoding as string) ?? null);
      }
      return await request(method, data);
    };
    const status = computed<boolean>((): boolean => (('Notification' in window) ? (Notification.permission === 'granted') : false));
    // Function to check permission for notification (can be sent multiple times without any unwanted effects)
    const ask = async (e?: Event | undefined): Promise<void> => {
      e?.preventDefault();
      initDataCookie();
      const permission = Notification.permission;
      if (permission === 'denied')
        await unsubscribe();
      else if (permission === 'granted')
        await subscribe();
      else if (permission === 'default') {
        await unsubscribe();
        await Notification.requestPermission()
          ?.then(async (permissionResult: NotificationPermission) => {
          if (permissionResult === 'granted')
            return await subscribe();
        });
        reloadNuxtApp();
      }
    };
    // Assign result
    const result = ref<INotification>({
      status: status,
      subscribe: subscribe,
      unsubscribe: unsubscribe,
      ask: ask
    });
    // Usage
    await nuxtApp.vueApp?.use({
      install(app: any, options: any) {
        app.provide('notification', result as Ref<INotification>);
      }
    });
    // App
    nuxtApp.hook('app:mounted', async () => {
      // This logic will run only once after the root Nuxt app is mounted
      if (import.meta.client)
        try {
          await result?.value?.ask();
        } catch (error) {
          console.error('Error during single Service Worker setup:', error);
        }
    });

    return {
      provide: {
        notification: result as Ref<INotification>
      }
    };
  },
  env: {
    islands: true
  }
});
